import { objectUtils, type Optional } from '@festina-life/core'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import cx from 'classnames'
import {
  type ComponentProps,
  type MouseEvent,
  type ReactNode,
  useState,
} from 'react'

import { useTranslate } from '../../hooks/useTranslate'
import { dropdownStyles } from '../../libs/dropdownStyles'
import { Icon } from '../../structure/icon/Icon'
import { selectStyles } from '../select/selectStyles'
import { rawTextInputStyles } from '../textInput/rawTextInputStyles'
import { MultiSelectedOptions } from './MultiSelectedOptions'

type InputProps = Required<ComponentProps<'input'>>
type ButtonOnClickEvent = MouseEvent<HTMLButtonElement> | undefined

type Props<T> = {
  /**
   * Classname applied to the container element
   */
  className?: string
  /**
   * Classname applied to each option in the select. Use to change e.g. break behaviour
   */
  optionClassName?: string
  /**
   * Classname applied to the dropdown in the select. Use to change e.g. width
   */
  optionsClassName?: string
  /**
   * The options presented in the select. If the presented text differs from the actual value.
   */
  options: readonly T[]
  /**
   * The selected value
   */
  values?: T[]
  /**
   * If the input should remember previous values
   */
  autoComplete?: boolean
  /**
   * If the input should be disabled
   * @defaultValue `false`
   */
  disabled?: boolean
  /**
   * A callback to handle when a option is selected
   */
  onChange: (event: ButtonOnClickEvent, value: T[]) => void
  /**
   * A callback to pass the current value up when onBlur is called.
   * This is needed since the FocusEvent from <ComboboxInput> where the onBlur is called does not have the value.
   */
  onBlurValue?: (value: T[] | undefined) => void
  /**
   * A callback to handle when clicking outside of the input component
   */
  onBlur?: InputProps['onBlur']
  /**
   * If the select is not editable it just displays the selected value as a string
   */
  editable?: boolean
  /**
   * Displays the component in an error state
   */
  isInError?: boolean
  /**
   * Function for rendering the contents of an item in the list
   */
  renderFn?: (items: T) => string | ReactNode
  /**
   * Function for retrieving the key for an item in the list
   */
  keyFn?: (items: T) => string | undefined
  /**
   * Function to listen for a key up event on the input
   */
  onInputKeyUp?: InputProps['onKeyUp']
  /**
   * Whether the MultiSelect supports local filtering of the options as the user types
   * @defaultValue `true`
   */
  isLocalFilterEnabled?: boolean
  /**
   * A callback to handle when the query text changes, after local filtering was done if needed
   */
  onQueryChange?: (query: string) => void
  /**
   * True if click events should not propagate.
   */
  isPropagationStopped?: boolean
  /**
   * The id of the input element
   */
  id?: string
} & (
  | {
      /**
       * Creates a generic type T from an input string. This is the reverse of the renderFn and should only be provided
       * when isValueCreator is true.
       */
      valueFromString: (value: string) => T
      isValueCreator: true
    }
  | {
      valueFromString?: never
      isValueCreator?: never
    }
)

/**
 * MultiSelect component to work somewhat like an autocomplete component.
 * It displays the selected value and allows the user to select a new value.
 * It also displays a dropdown of options to select from.
 * It also allows the user to type in a new value. That is not part of the dropdown.
 */
export const RawMultiSelect = <T,>({
  options,
  disabled = false,
  isInError = false,
  values,
  autoComplete,
  editable = true,
  onBlurValue,
  onChange,
  onBlur,
  renderFn = (entry) => (entry ? `${entry}` : ''),
  keyFn = (entry: T) => objectUtils.objectToSortedString(entry),
  isLocalFilterEnabled = true,
  onQueryChange,
  isPropagationStopped = false,
  isValueCreator,
  valueFromString = (value: string) => value as T,
  className,
  optionClassName,
  optionsClassName,
  id,
}: Props<T>) => {
  const translate = useTranslate()
  const [query, setQuery] = useState('')

  const filteredOptions =
    query === '' || !isLocalFilterEnabled
      ? options
      : options.filter((option) =>
          (renderFn(option) as string)
            ?.toLowerCase()
            .includes(query.toLowerCase()),
        )

  const handleOnChange = (value: Optional<T>) => {
    const doesItemAlreadyExist =
      value && values?.some((x) => keyFn(x) === keyFn(value))
    setQuery('')
    const valueList = value ? [value] : []

    if (values === undefined) {
      onChange(undefined, valueList)
      onBlurValue?.(valueList)
    } else if (doesItemAlreadyExist) {
      const withoutRemovedItem = values.filter((x) => keyFn(x) !== keyFn(value))
      onChange(undefined, withoutRemovedItem)
      onBlurValue?.(withoutRemovedItem)
    } else {
      const newValue = [...values, ...valueList]
      onChange(undefined, newValue)
      onBlurValue?.(newValue)
    }
  }

  const isShowingQueryAsOption = isValueCreator && !!query.length

  return editable ? (
    <div className={className}>
      <Combobox onChange={(value) => handleOnChange(value as Optional<T>)}>
        {/* Wrapping the input in a button to open the combobox when selected */}
        <ComboboxButton
          className={cx('relative w-full')}
          onClick={
            isPropagationStopped ? (evt) => evt.stopPropagation() : undefined
          }
        >
          <ComboboxInput
            data-autofocus
            autoComplete={autoComplete ? 'on' : 'off'}
            onClick={
              isPropagationStopped ? (evt) => evt.stopPropagation() : undefined
            }
            className={cx(
              rawTextInputStyles({ isInError, disabled }),
              'pe-5', // To leave space for the chevrons
            )}
            displayValue={() => ''}
            onKeyDown={(e) => e.stopPropagation()} // Prevents the space key is propagating up to the button and triggering a click. https://github.com/tailwindlabs/headlessui/discussions/1687#discussioncomment-10541485
            onChange={(e) => {
              setQuery(e.target.value)
              onQueryChange?.(e.target.value)
            }}
            onBlur={onBlur}
            // Setting to disable is not sufficient as it will still allow the user to type in a new value. Therefor we set it to readOnly so no editation can be made.
            readOnly={disabled}
            data-testid={`multiselect-input-for-${id}`}
          />
          <div className={selectStyles.inputIconsWrapper}>
            <Icon
              name="ChevronUpDown"
              title="" // deliberately empty since the icon is decorative only
            />
          </div>
        </ComboboxButton>
        <ComboboxOptions
          transition
          className={cx(
            selectStyles.options,
            'min-w-(--input-width) max-w-[min(calc(var(--input-width)*3),90vw)]!',
            optionsClassName,
            dropdownStyles,
          )}
          anchor="bottom start"
        >
          {isShowingQueryAsOption && (
            <ComboboxOption
              value={valueFromString(query)}
              className={({ focus, disabled, selected }) =>
                cx(
                  selectStyles.option({
                    hasFocus: focus,
                    isSelected: selected,
                    isDisabled: disabled,
                  }),
                  optionClassName,
                )
              }
            >
              {query}
            </ComboboxOption>
          )}
          {filteredOptions.length === 0 && !isShowingQueryAsOption ? (
            <div className="cursor-default select-none px-2 py-1.5">
              {isValueCreator
                ? translate('input.startToAdd')
                : translate('input.nothingFound')}
            </div>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = !!values?.some(
                (x) => keyFn(x) === keyFn(option),
              )

              return (
                <ComboboxOption
                  key={keyFn(option)}
                  className={({ focus, disabled }) =>
                    cx(
                      selectStyles.option({
                        hasFocus: focus,
                        isSelected,
                        isDisabled: disabled,
                      }),
                      optionClassName,
                    )
                  }
                  value={option}
                  onClick={
                    isPropagationStopped
                      ? (evt) => evt.stopPropagation()
                      : undefined
                  }
                  disabled={disabled}
                >
                  {renderFn(option)}
                  <Icon
                    name="Check"
                    title="" // deliberately empty since the icon is decorative only
                    className={cx(
                      'ms-auto',
                      isSelected ? 'opacity-100!' : 'opacity-0!',
                    )}
                    color="unset"
                  />
                </ComboboxOption>
              )
            })
          )}
        </ComboboxOptions>
        {/* Should be moved outside once the RawCombobox has incorporated the logic to handle multiple options */}
        <MultiSelectedOptions
          onChange={onChange}
          renderFn={renderFn}
          keyFn={keyFn}
          values={values}
          editable={editable}
        />
      </Combobox>
    </div>
  ) : (
    <span>{values ? values.join(', ') : '-'}</span>
  )
}
